/**
 * PageHeader — the one page title. Replaces the five heading styles the
 * 2026-10-03 review counted (`cine-h` serif, `text-2xl font-semibold`,
 * `text-xl`, `font-serif font-light`, `text-3xl`).
 *
 *     <PageHeader
 *       title="Matters"
 *       description="Every open matter in your firm."
 *       actions={<Button>New matter</Button>}
 *     />
 *
 * - `title` renders as the page's only `<h1>`. Keep it a name, not a pitch.
 * - `description` is one sentence, solid colour, ≥ 14 px. No marketing copy,
 *   no promise the API does not keep.
 * - `breadcrumb` is a slot: the shell already draws the menu trail above
 *   every page, so pass one only for a deeper trail (a record inside a
 *   record).
 * - `aiAssistedNote` is the server's `ai_assisted_note` sentence; it renders
 *   only when present (see AiAssistedNote).
 * - The root adds no `mx-auto` / `max-w` / `px` — the shell owns the column.
 */
import type { ReactNode } from 'react';

import { AiAssistedNote } from './AiAssistedNote';
import { cn } from './cn';

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Small label above the title (a section name). Optional; use sparingly. */
  eyebrow?: ReactNode;
  breadcrumb?: ReactNode;
  /** Buttons / links on the right (stacked under the title on phones). */
  actions?: ReactNode;
  /** The API's `ai_assisted_note`; nothing renders when it is empty. */
  aiAssistedNote?: string | null;
  /** Inline content after the title, e.g. a status Badge. */
  titleAdornment?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  eyebrow,
  breadcrumb,
  actions,
  aiAssistedNote,
  titleAdornment,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn('mb-6 space-y-2', className)}>
      {breadcrumb ? <div className="text-sm text-fg-muted">{breadcrumb}</div> : null}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          {eyebrow ? (
            <p className="text-xs font-medium uppercase tracking-wide text-gold-text">{eyebrow}</p>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight text-fg">{title}</h1>
            {titleAdornment}
          </div>
          {description ? (
            <p className="max-w-reading text-sm text-fg-secondary">{description}</p>
          ) : null}
          {aiAssistedNote ? <AiAssistedNote note={aiAssistedNote} /> : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>
    </header>
  );
}
